import React, { memo } from 'react';
import FilePreviewRenderer from '../../../common/functions/FilePreviewRenderer';
import notify from '../../../common/Utils/toastService';
import styles from '../../../styles/TryNow.module.css';
import { TRY_MODE } from '../../../common/Utils/Utils';

const UploadSection = memo(
  ({
    selectedFile,
    fileInputRef,
    handleFileSelection,
    productConfig,
    isDisabled = false,
    mode = TRY_MODE.SINGLE,
  }) => {
    const maxFileSize = productConfig?.uiConfig?.maxFileSize || 5242880; // Default 5MB
    const acceptFileTypes =
      productConfig?.uiConfig?.acceptFileTypes ||
      'image/*,application/pdf,.tif,.tiff';

    const handleDrop = (e) => {
      e.preventDefault();
      if (isDisabled) return; // Don't allow drop if disabled
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        handleFileSelection(file);
      }
    };

    const handleInputChange = (e) => {
      if (isDisabled) return; // Don't allow file selection if disabled
      const file = e.target.files[0];
      if (validateFile(file)) {
        handleFileSelection(file);
      } else {
        e.target.value = '';
      }
    };

    const validateFile = (file) => {
      if (!file) return false;

      // Check file type
      if (!isSupportedFile(file)) {
        showInvalidFileToast();
        return false;
      }

      // Check file size
      if (file.size > maxFileSize) {
        const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(2);
        notify.error(
          `File size exceeds the maximum limit of ${maxSizeMB}MB. Please upload a smaller file.`
        );
        return false;
      }

      return true;
    };

    // Parse acceptFileTypes string into array and check if file is supported
    const isSupportedFile = (file) => {
      if (!file) return false;

      // Convert acceptFileTypes string to array: "image/*,application/pdf,.tif" -> ["image/*", "application/pdf", ".tif"]
      const acceptedTypesArray = acceptFileTypes
        .split(',')
        .map((type) => type.trim());

      const fileName = file.name?.toLowerCase() || '';
      const fileType = file.type?.toLowerCase() || '';

      // Check if file matches any accepted type
      for (const acceptedType of acceptedTypesArray) {
        const lowerAcceptedType = acceptedType.toLowerCase();

        // Match wildcard: "image/*" matches "image/png", "image/jpeg", etc.
        if (lowerAcceptedType.includes('/*')) {
          const category = lowerAcceptedType.replace('/*', '');
          if (fileType.startsWith(category)) {
            return true;
          }
        }
        // Match extension: ".pdf", ".tif", ".tiff"
        else if (lowerAcceptedType.startsWith('.')) {
          if (fileName.endsWith(lowerAcceptedType)) {
            return true;
          }
        }
        // Match MIME type: "application/pdf"
        else {
          if (fileType === lowerAcceptedType) {
            return true;
          }
        }
      }

      return false;
    };

    // Generate dynamic error message based on accepted file types
    const showInvalidFileToast = () => {
      const acceptedTypes = acceptFileTypes
        .split(',')
        .map((type) => type.trim())
        .map((type) => {
          // Convert MIME types to readable format
          if (type.includes('/*')) {
            return type.split('/')[0]; // "image/*" -> "IMAGE"
          }
          if (type.startsWith('.')) {
            return type; // ".pdf" -> ".PDF"
          }
          // "application/pdf" -> "PDF"
          const parts = type.split('/');
          return parts[parts.length - 1];
        });

      // Remove duplicates and format message
      const uniqueTypes = [...new Set(acceptedTypes)];
      const typesString =
        uniqueTypes.length > 1
          ? uniqueTypes.slice(0, -1).join(', ') +
            ' and ' +
            uniqueTypes[uniqueTypes.length - 1]
          : uniqueTypes[0];

      notify.error(`Only ${typesString} files are supported`);
    };
    return (
      <div className="h-100">
        <div className="d-flex flex-column h-100">
          <div
            className="uploadSection"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height:
                mode === TRY_MODE.DUAL
                  ? 'calc(100svh - 302px)'
                  : 'calc(100svh - 262px)',
              padding: '10px',
              // opacity: isDisabled ? 0.6 : 1,
              cursor: isDisabled ? 'default' : 'pointer',
              // pointerEvents: isDisabled ? 'none' : 'auto',
            }}
            onClick={() => !isDisabled && fileInputRef.current.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept={acceptFileTypes}
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleInputChange}
              disabled={isDisabled}
            />
            {selectedFile ? (
              <FilePreviewRenderer
                file={selectedFile}
                originalFileType={selectedFile.type}
              />
            ) : (
              <div className="d-flex flex-column align-items-center">
                <div className="uploadFileIcon bg_master2 d-flex justify-content-center rounded-circle mb-3 align-items-center">
                  <i className="fas fa-upload text-white font_25"></i>
                </div>
                <h3 className="font_18 font_500">Upload Document</h3>
                <p className={styles.uploadText}>
                  Drag and drop your document here or
                </p>
                <div className="d-flex align-items-center gap-2 bg-black px-3 py_13x rounded-pill text-white">
                  <i className="fas fa-plus text-white"></i>
                  Choose File
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

UploadSection.displayName = 'UploadSection';
export default UploadSection;
